import { type CSSProperties, type ReactNode, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
} from '@dnd-kit/core';

export type DragHandleProps = DraggableAttributes &
  NonNullable<DraggableSyntheticListeners>;
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export const useSortableItem = (id: string) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging && {
      opacity: 0.5,
      zIndex: 1,
      cursor: 'grabbing',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    }),
  };

  return { setNodeRef, style, attributes, listeners, isDragging };
};

type SortableGridProps<T> = {
  items: T[];
  getId: (item: T) => string;
  onReorder: (items: T[]) => void;
  children: ReactNode;
};

export const SortableGrid = <T,>({
  items,
  getId,
  onReorder,
  children,
}: SortableGridProps<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => getId(item) === active.id);
      const newIndex = items.findIndex((item) => getId(item) === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      onReorder(arrayMove(items, oldIndex, newIndex));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map(getId).filter((id): id is string => id != null)}
        strategy={rectSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
};

export const useSortableGroup = (id: string) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Hide the original; DragOverlay renders the visible clone
    ...(isDragging && { opacity: 0 }),
  };
  return {
    setNodeRef,
    style,
    isDragging,
    dragHandleProps: { ...attributes, ...(listeners ?? {}) } as DragHandleProps,
  };
};

type SortableListProps<T> = SortableGridProps<T> & {
  renderOverlay?: (activeId: string) => ReactNode;
};

export const SortableList = <T,>({
  items,
  getId,
  onReorder,
  children,
  renderOverlay,
}: SortableListProps<T>) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => getId(item) === active.id);
      const newIndex = items.findIndex((item) => getId(item) === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      onReorder(arrayMove(items, oldIndex, newIndex));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => setActiveId(String(active.id))}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map(getId).filter((id): id is string => id != null)}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
      {renderOverlay && (
        <DragOverlay>{activeId ? renderOverlay(activeId) : null}</DragOverlay>
      )}
    </DndContext>
  );
};

type SortableGroupWrapperProps = {
  id: string;
  children: (dragHandleProps: DragHandleProps) => ReactNode;
};

export const SortableGroupWrapper = ({
  id,
  children,
}: SortableGroupWrapperProps) => {
  const { setNodeRef, style, dragHandleProps } = useSortableGroup(id);
  return (
    <div ref={setNodeRef} style={style}>
      {children(dragHandleProps)}
    </div>
  );
};
